import { apiService } from './apiService';

/**
 * WU-05 matching-engine API client (flagged: backend `reco.matching`).
 * Mirrors the house apiService conventions (bearer token, base URL, {data,error}
 * envelope). Fails SOFT by design: when the reco surface is off or not deployed the
 * endpoint returns 404, and every method here returns null/empty so the dashboard
 * silently falls back to its existing SignalR room list. Never throws.
 *
 * Backed by POST /api/reco/{rooms,people,group}.
 */

export interface RecoRelevance {
  /** 0..1 rounded composite relevance. */
  score: number;
  /** User-visible reason lines only (shared interests, group size, recency) — never raw traits/scores. */
  reasons: string[];
  /** True when this slot is a serendipity/exploration pick rather than a top-ranked one. */
  explore: boolean;
}

export interface RecoRoom {
  lobbyId: string;
  name: string;
  category: string;
  subCategory?: string | null;
  sceneTemplateId?: string | null;
  humanCount: number;
  maxPlayers: number;
  relevance: RecoRelevance;
}

export interface RecoRoomsResult {
  /** Cold-start honesty rung: "Full" | "InterestsAndEmbedding" | "PopularityBlend". */
  basis: string;
  rooms: RecoRoom[];
}

class RecoService {
  /**
   * Ranked live rooms with per-room relevance. Returns null when the reco surface is
   * disabled/undeployed (404) or on any error — the caller treats null as "no reco, use
   * the plain room list."
   */
  async getRoomRecommendations(count = 12): Promise<RecoRoomsResult | null> {
    const res = await apiService.post<RecoRoomsResult>('/api/reco/rooms', { count });
    if (res.error || !res.data) return null;
    return res.data;
  }
}

export const recoService = new RecoService();
